/**
 * ShiftRosterBlock
 *
 * Renders ONE collapsed emerald block in place of 3+ cramped overlapping shift
 * columns. Shows the cluster's span, a canvasser count, and an avatar-initials
 * stack. Clicking the block opens a shadcn Popover listing each shift as a row
 * (avatar initials + name + its own time range + turf + status badge); clicking
 * a row calls the existing shift-click handler.
 *
 * Positioning math + emerald status vocabulary are copied from ShiftBlock so the
 * roster block sits in the grid identically to a normal shift block.
 */

import { useState } from 'react';
import { Users, ChevronRight } from 'lucide-react';
import type { ShiftListItemDto } from '@signalvote/shared';
import { ShiftStatus, assertNever } from '@signalvote/shared';
import {
  HOUR_HEIGHT,
  formatTime12Hour,
  minutesToTimeString,
  timeToMinutes,
  type EventPosition,
} from '@/lib/calendarUtils';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DEFAULT_SHIFT_MINUTES, type ShiftRosterGroup } from './shiftRoster';
import { getShiftStatusMeta } from './shiftStatusVocab';
import { SHIFT_HOVER_CLASSES, cascadeStackClass } from './shiftBlockClasses';

// ============================================================================
// Status vocabulary (emerald — badge/chip classes local to the roster layout)
// ============================================================================
//
// The per-status icon comes from the shared `getShiftStatusMeta` (single source
// of truth). The label + badge/chip color classes stay here because they are
// roster-specific: the roster renders a lowercase "In progress" and small inline
// badge/avatar classes rather than the full block styling ShiftBlock uses.

interface RosterStatusMeta {
  label: string;
  /** Small inline badge classes for the roster-list rows. Icon + label, never color-only. */
  badge: string;
  /** Avatar-initials chip classes (stack + roster row). */
  chip: string;
}

function getRosterStatusMeta(status: ShiftStatus): RosterStatusMeta {
  switch (status) {
    case ShiftStatus.Available:
      return {
        label: 'Available',
        badge: 'bg-emerald-100 text-emerald-800 border border-emerald-300',
        chip: 'bg-emerald-50 text-emerald-800',
      };
    case ShiftStatus.Claimed:
      return {
        label: 'Claimed',
        badge: 'bg-emerald-600 text-white border border-emerald-700',
        chip: 'bg-emerald-600 text-white',
      };
    case ShiftStatus.InProgress:
      return {
        label: 'In progress',
        badge: 'bg-emerald-700 text-white border border-emerald-800',
        chip: 'bg-emerald-700 text-white',
      };
    case ShiftStatus.Completed:
      return {
        label: 'Completed',
        badge: 'bg-emerald-200 text-emerald-800 border border-emerald-400',
        chip: 'bg-emerald-200 text-emerald-800',
      };
    default:
      return assertNever(status);
  }
}

// ============================================================================
// Helpers
// ============================================================================

/** Two-letter initials from a display name, e.g. "Aria Boone" => "AB". */
function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** The person to show for a shift: claimer, then assignee, then the shift name. */
function shiftLabel(shift: ShiftListItemDto): string {
  return shift.claimedByName ?? shift.assignedToName ?? shift.name;
}

/** The shift's own clock range, e.g. "9 AM - 11 AM". Staggered clusters collapse
 *  into one block, so a row can't inherit the block's span. */
function shiftRange(shift: ShiftListItemDto): string | undefined {
  if (!shift.startTime) return undefined;
  const start = timeToMinutes(shift.startTime);
  const end = start + (shift.estimatedMinutes || DEFAULT_SHIFT_MINUTES);
  return `${formatTime12Hour(minutesToTimeString(start))} - ${formatTime12Hour(minutesToTimeString(end))}`;
}

/** The turf/walkbook line under the person's name. */
function shiftTurf(shift: ShiftListItemDto): string | undefined {
  return shift.walkbookName ?? undefined;
}

// ============================================================================
// Sub-components
// ============================================================================

interface StatusBadgeProps {
  status: ShiftStatus;
}

/** Inline status pill — icon + label, never color-only. */
function StatusBadge({ status }: StatusBadgeProps) {
  const meta = getRosterStatusMeta(status);
  const Icon = getShiftStatusMeta(status).icon;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold',
        meta.badge
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {meta.label}
    </span>
  );
}

interface AvatarStackProps {
  shifts: ShiftListItemDto[];
  getShiftStatus: (id: string) => ShiftStatus;
  cap?: number;
}

/** Stacked avatar initials with a "+N" overflow chip. */
function AvatarStack({ shifts, getShiftStatus, cap = 4 }: AvatarStackProps) {
  const shown = shifts.slice(0, cap);
  const extra = shifts.length - shown.length;
  return (
    <div className="flex items-center">
      <div className="flex -space-x-2">
        {shown.map((shift) => {
          const meta = getRosterStatusMeta(getShiftStatus(shift.id));
          return (
            <div
              key={shift.id}
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-full text-[0.65rem] font-semibold ring-2 ring-white',
                meta.chip
              )}
              title={shiftLabel(shift)}
            >
              {initials(shift.claimedByName ?? shift.assignedToName)}
            </div>
          );
        })}
      </div>
      {extra > 0 && (
        <span className="ml-1 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[0.65rem] font-semibold text-emerald-800">
          +{extra}
        </span>
      )}
    </div>
  );
}

interface RosterRowProps {
  shift: ShiftListItemDto;
  status: ShiftStatus;
  onClick: (shift: ShiftListItemDto) => void;
}

/** One shift per row: avatar, name, turf, status badge. Clicking calls onShiftClick. */
function RosterRow({ shift, status, onClick }: RosterRowProps) {
  const meta = getRosterStatusMeta(status);
  const label = shiftLabel(shift);
  const turf = shiftTurf(shift);
  const range = shiftRange(shift);
  return (
    <button
      type="button"
      onClick={() => onClick(shift)}
      className="flex w-full items-center gap-3 rounded-md px-1 py-2 text-left transition-colors hover:bg-emerald-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
    >
      <span
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
          meta.chip
        )}
      >
        {initials(shift.claimedByName ?? shift.assignedToName)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground">
          {label}
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {[range, turf].filter(Boolean).join(' · ')}
        </span>
      </span>
      <StatusBadge status={status} />
    </button>
  );
}

// ============================================================================
// Main component
// ============================================================================

export interface ShiftRosterBlockProps {
  group: ShiftRosterGroup;
  position?: EventPosition;
  getShiftStatus: (id: string) => ShiftStatus;
  onShiftClick: (shift: ShiftListItemDto) => void;
  /** True when this is the cascaded block the user last clicked, so it sits in front. */
  isRaised?: boolean;
  /** Called when the user opens this block, so the column can raise it. */
  onRaise?: () => void;
}

export function ShiftRosterBlock({
  group,
  position,
  getShiftStatus,
  onShiftClick,
  isRaised = false,
  onRaise,
}: ShiftRosterBlockProps) {
  const [open, setOpen] = useState(false);

  // Opening the roster is this block's equivalent of clicking a shift, so it
  // commits the block to the front of a cascade the same way.
  const handleOpenChange = (next: boolean) => {
    if (next) onRaise?.();
    setOpen(next);
  };

  // Grid positioning — identical math to ShiftBlock (top/height from minutes).
  const top = (group.startMinutes / 60) * HOUR_HEIGHT;
  const height = ((group.endMinutes - group.startMinutes) / 60) * HOUR_HEIGHT;

  // Side-by-side left/width from the packing algorithm, matching ShiftBlock.
  const positionStyle = position
    ? { left: position.left, width: position.width }
    : { left: '2px', width: 'calc(100% - 4px)' };

  const stackingClass = cascadeStackClass(position?.zIndex, isRaised);

  const count = group.shifts.length;
  const startStr = formatTime12Hour(minutesToTimeString(group.startMinutes));
  const endStr = formatTime12Hour(minutesToTimeString(group.endMinutes));
  const rangeLabel = `${startStr} - ${endStr}`;

  // Stop clicks from bubbling to the day-column body (empty-slot handler) or any
  // other click-handling ancestor. See memory:
  // project_popover_portal_click_bubbles_react_tree.
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div
          role="button"
          tabIndex={0}
          aria-label={`View roster: ${count} canvassers, ${rangeLabel}`}
          data-event-block
          onClick={stop}
          className={cn(
            'absolute flex min-h-[44px] cursor-pointer flex-col justify-between overflow-hidden rounded-xl p-1.5',
            'border-[1.5px] border-emerald-500 bg-emerald-50 shadow-sm',
            'hover:bg-emerald-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500',
            SHIFT_HOVER_CLASSES,
            stackingClass
          )}
          style={{ top, height, ...positionStyle }}
        >
          <div className="flex items-center justify-between text-emerald-800">
            <span className="inline-flex items-center gap-1 text-[0.7rem] font-semibold">
              <Users className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{rangeLabel}</span>
            </span>
            <ChevronRight className="h-4 w-4 shrink-0 text-emerald-600" />
          </div>

          <p className="mt-1 text-sm font-semibold leading-tight text-emerald-900">
            {count} canvasser{count !== 1 ? 's' : ''}
          </p>

          {height >= 70 && (
            <div className="mt-1">
              <AvatarStack shifts={group.shifts} getShiftStatus={getShiftStatus} />
            </div>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        onClick={stop}
        className="w-80 max-w-[calc(100vw-2rem)]"
      >
        <p className="text-sm font-semibold text-foreground">{rangeLabel}</p>
        <p className="mb-2 text-xs text-muted-foreground">
          {count} canvasser{count !== 1 ? 's' : ''} out
        </p>
        <div className="max-h-80 divide-y divide-border overflow-y-auto">
          {group.shifts.map((shift) => (
            <RosterRow
              key={shift.id}
              shift={shift}
              status={getShiftStatus(shift.id)}
              onClick={onShiftClick}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default ShiftRosterBlock;
